import React from "react";
import { ArrowUpFromDot, BoxSelect, Grid3X3, ScanEye, Sparkles } from "lucide-react";
import { InspectorVisibilityEyeButton } from "../components/ui/InspectorVisibilityEyeButton";
import { InspectorVisibilitySection } from "../components/property-panels/InspectorVisibilitySection";
import VisibilityIconRow from "../components/property-panels/VisibilityIconRow";
import { useMeshVisibilityControls } from "./useMeshVisibilityControls";
import { useViewerActions, useViewerStore } from "../stores/viewerStore";
import { useSelectionHighlightControl } from "../selected-highlight-visibility";

const MeshVisibilityToolbar = ({
  meshUuid,
  bodyUuid,
  className = "",
  sectionTitle = "Mesh Visibility",
  highlightTargetType = "mesh",
  highlightTargetId = meshUuid,
}) => {
  const meshVisibility = useViewerStore((s) => s.meshVisibility);
  const viewerActions = useViewerActions();
  const thisMeshVisible = meshUuid ? (meshVisibility[meshUuid] ?? true) : true;
  const {
    state,
    disabled,
    focusDisabled,
    toggleFocus,
    toggleWireframe,
    toggleBoundingBox,
    toggleOriginMarker,
    setWireframeColor,
  } = useMeshVisibilityControls({ meshUuid, bodyUuid, enabled: true });
  const { highlight, toggleHighlight } = useSelectionHighlightControl({
    targetType: highlightTargetType,
    targetId: highlightTargetId,
    disabled,
    ensureVisible: () => {
      if (!useViewerStore.getState().showVisual) viewerActions.toggleShowVisual();
    },
  });

  const buttons = [
    {
      key: "highlight",
      title:
        highlightTargetType === "visualGeom"
          ? "Highlight selected visual geom"
          : "Highlight selected mesh",
      on: highlight,
      onClick: toggleHighlight,
      disabled,
      Icon: Sparkles,
    },
    {
      key: "focus",
      title: "Display selected only",
      on: state.focus,
      onClick: toggleFocus,
      disabled: focusDisabled,
      Icon: ScanEye,
    },
    {
      key: "wireframe",
      title: "Wireframe overlay",
      on: state.wireframe,
      onClick: toggleWireframe,
      disabled,
      Icon: Grid3X3,
    },
    {
      key: "bbox",
      title: "Bounding box",
      on: state.boundingBox,
      onClick: toggleBoundingBox,
      disabled,
      Icon: BoxSelect,
    },
    {
      key: "origin",
      title: "Mesh origin marker (dot + positive-axis L + up arrow)",
      on: state.originMarker,
      onClick: toggleOriginMarker,
      disabled,
      Icon: ArrowUpFromDot,
    },
  ];

  return (
    <InspectorVisibilitySection
      sectionTitle={sectionTitle}
      className={className}
      headerActions={
        <div className="flex items-center gap-1">
          <InspectorVisibilityEyeButton
            visible={thisMeshVisible}
            onToggle={() => meshUuid && viewerActions.setMeshVisibility(meshUuid, !thisMeshVisible)}
            tooltipWhenOn="This mesh is visible — click to hide"
            tooltipWhenOff="This mesh is hidden — click to show"
            ariaLabel="Toggle this mesh's visibility"
          />
        </div>
      }
    >
      <VisibilityIconRow actions={buttons} />
      {state.wireframe && !disabled && (
        <div className="flex items-center gap-2 px-0.5">
          <span className="text-xs text-gray-400 shrink-0 select-none">Wireframe color</span>
          <input
            type="color"
            value={state.wireframeColor}
            onChange={(e) => setWireframeColor(e.target.value)}
            className="w-9 h-7 border border-gray-600 rounded cursor-pointer bg-transparent"
            aria-label="Wireframe color"
          />
        </div>
      )}
    </InspectorVisibilitySection>
  );
};

export default React.memo(MeshVisibilityToolbar);
